import React, { ReactElement } from "react";
import { Heading, Icon, Badge, Flex, Text } from "@telia/teddy";
import "./RetailerSubscriptionCard.less";
import PAGES from "../../../../../../constants/pages";
import { getStatusBadge } from "../../../utils";
import { ChangeScope } from "../../../../../../models/state/retailer";

interface Props {
    changedFromName?: string;
    disableNavigation?: boolean;
    familyDiscount?: boolean;
    yearlyCommitmentLabel?: string;
    id: string;
    isActive?: boolean;
    isChanged?: boolean;
    changeScope?: ChangeScope;
    name?: string | ReactElement;
    price: string | number | ReactElement;
    priceStriketrough?: string | number;
    priceSuffix?: string;
    status?: string;
    strikethroughTitle?: string;
    title?: string;
}

const RetailerSubscriptionCard: React.FC<Props> = ({
    changedFromName,
    disableNavigation,
    familyDiscount,
    yearlyCommitmentLabel,
    id,
    name,
    title,
    price,
    priceStriketrough,
    priceSuffix = ",-/md.",
    strikethroughTitle,
    isActive,
    isChanged,
    changeScope,
    status,
}) => {
    const statusBadge = getStatusBadge(status, isActive, isChanged, changeScope);
    return (
        <div
            tabIndex={disableNavigation ? undefined : 0}
            role={disableNavigation ? undefined : "link"}
            className={`subscriptionCard${disableNavigation ? " subscriptionCard--static" : ""}`}
            onClick={
                disableNavigation
                    ? undefined
                    : () => window.location.assign(`${PAGES.RETAILER.ORDER.SUBSCRIPTION.URL}?subscription=${id}`)
            }
        >
            {statusBadge && (
                <Badge hideIcon variant={statusBadge.variant} className="subscriptionCard__badge">
                    {statusBadge.text}
                </Badge>
            )}
            <div className="subscriptionCard__headingWrapper">
                <div className="subscriptionCard__heading">
                    <div className="subscriptionCard__container">
                        <div className="subscriptionCard__content">
                            <div>
                                <div className="subscriptionCard__nameWrapper">
                                    <Heading mb="100" as="h2" variant="title-200" className="subscriptionCard__name">
                                        {strikethroughTitle && (
                                            <span className="subscriptionCard__strikethroughTitle">
                                                {strikethroughTitle}
                                            </span>
                                        )}
                                        {name || "Nytt abonnement"}
                                    </Heading>
                                </div>
                                {changedFromName && (
                                    <Flex gapX="100" mt="50">
                                        <Text variant="paragraph-100">Endret fra {changedFromName}</Text>
                                    </Flex>
                                )}
                                <span dangerouslySetInnerHTML={{ __html: title || "Ikke ferdig registrert enda" }} />
                            </div>
                            <div className="subscriptionCard__priceWrapper">
                                {price && (
                                    <Heading
                                        mb="100"
                                        as="h2"
                                        variant="title-200"
                                        title={
                                            typeof price === "string" || typeof price === "number"
                                                ? `${price}`
                                                : undefined
                                        }
                                        className="subscriptionCard__price"
                                    >
                                        {price}
                                        {typeof price === "string" || typeof price === "number" ? priceSuffix : null}
                                    </Heading>
                                )}
                                {priceStriketrough && (
                                    <div className="subscriptionCard__priceStriketroughWrapper">
                                        <span className="subscriptionCard__priceStriketroughDisclaimer">
                                            ord. pris:
                                        </span>
                                        <div className="subscriptionCard__priceStriketrough">
                                            {priceStriketrough}
                                            {priceSuffix}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {!disableNavigation && (
                            <div className="subscriptionCard__actions">
                                <Icon name="chevron-right" size="sm" aria-hidden="true" focusable="false" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {(familyDiscount || yearlyCommitmentLabel) && (
                <div className="subscriptionCard__badgesRow">
                    {familyDiscount && (
                        <Badge hideIcon variant="special">
                            Familierabatt
                        </Badge>
                    )}
                    {yearlyCommitmentLabel && (
                        <Badge hideIcon variant="information">
                            {yearlyCommitmentLabel}
                        </Badge>
                    )}
                </div>
            )}
        </div>
    );
};

export default RetailerSubscriptionCard;
